import type { IndexCacheEntry, FrequencyMap } from "./types";

/**
 * Manages search result indices for efficient pagination and caching.
 * Key insight: Store only IDs, not full objects, for lightweight caching.
 */
export class SearchIndexManager<T extends { id: string }> {
  private cache: Map<string, IndexCacheEntry> = new Map();
  private frequencyMap: FrequencyMap = {};
  private readonly DEFAULT_KEY = "_default_ordering";
  private readonly MAX_CACHE_SIZE = 50; // Limit cached searches

  /**
   * Get or create an index for the given search query.
   * Returns array of IDs in sorted order.
   */
  getOrCreateIndex(
    query: string,
    items: T[],
    sortFn?: (items: T[]) => T[],
  ): string[] {
    const cacheKey = this.getCacheKey(query);
    const cached = this.cache.get(cacheKey);

    if (cached) {
      return cached.ids;
    }

    // Create new index
    let sortedItems: T[];
    if (!query && !sortFn) {
      // Default ordering: by frequency, then original order
      sortedItems = this.sortByFrequency(items);
    } else if (sortFn) {
      sortedItems = sortFn(items);
    } else {
      sortedItems = items; // Will be sorted by search function
    }

    const ids = sortedItems.map((item) => item.id);

    // Evict oldest if cache is full
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      this.evictOldest();
    }

    this.cache.set(cacheKey, {
      ids,
      timestamp: Date.now(),
      query,
    });

    return ids;
  }

  /**
   * Get items for a specific page using the index.
   * Ultra-fast: O(pageSize) lookup.
   */
  getPage<TItem extends { id: string }>(
    index: string[],
    items: TItem[],
    pageIndex: number,
    pageSize: number,
  ): TItem[] {
    const start = pageIndex * pageSize;
    const end = start + pageSize;
    const pageIds = index.slice(start, end);

    // Create lookup map for O(1) access
    const itemMap = new Map(items.map((item) => [item.id, item]));

    return pageIds
      .map((id) => itemMap.get(id))
      .filter((item): item is TItem => item !== undefined);
  }

  /**
   * Track item selection for frequency-based ordering.
   */
  trackSelection(itemId: string): void {
    this.frequencyMap[itemId] = (this.frequencyMap[itemId] || 0) + 1;
    // Invalidate default ordering cache when frequency changes
    this.cache.delete(this.getCacheKey(""));
  }

  /**
   * Sort items by frequency of use.
   */
  private sortByFrequency(items: T[]): T[] {
    return [...items].sort((a, b) => {
      const freqA = this.frequencyMap[a.id] || 0;
      const freqB = this.frequencyMap[b.id] || 0;
      return freqB - freqA; // Higher frequency first
    });
  }

  /**
   * Invalidate all cached indices (call on cache refresh).
   */
  invalidateAll(): void {
    this.cache.clear();
  }

  /**
   * Invalidate specific query cache.
   */
  invalidate(query: string): void {
    this.cache.delete(this.getCacheKey(query));
  }

  /**
   * Get current cache size.
   */
  getCacheSize(): number {
    return this.cache.size;
  }

  /**
   * Load frequency map from storage.
   */
  loadFrequencyMap(stored: FrequencyMap): void {
    this.frequencyMap = { ...stored };
  }

  /**
   * Export frequency map for persistence.
   */
  exportFrequencyMap(): FrequencyMap {
    return { ...this.frequencyMap };
  }

  private getCacheKey(query: string): string {
    return query.trim().toLowerCase() || this.DEFAULT_KEY;
  }

  private evictOldest(): void {
    let oldest: string | null = null;
    let oldestTime = Infinity;

    this.cache.forEach((entry, key) => {
      if (entry.timestamp < oldestTime && key !== this.DEFAULT_KEY) {
        oldest = key;
        oldestTime = entry.timestamp;
      }
    });

    if (oldest) {
      this.cache.delete(oldest);
    }
  }
}

// Singleton instances for products and partners
export const productSearchIndex = new SearchIndexManager<{ id: string }>();
export const partnerSearchIndex = new SearchIndexManager<{ id: string }>();
